import { posthog } from 'posthog-js';

/**
 * Funnel events for the public /browse map, which is where paid listing ads
 * land. Each carries the MLS home so a drop-off can be tied to a price band
 * or a specific ad card.
 */
export type BrowseEvent =
  | 'browse_listing_opened'
  | 'browse_tour_cta_clicked'
  | 'browse_listing_saved'
  | 'browse_signup_started'
  | 'browse_location_searched';

export interface BrowseEventProperties {
  mls_listing_id?: string;
  price?: number;
  city?: string;
  state?: string;
  /** Whether a jwtToken was present when the tour button was tapped. */
  authenticated?: boolean;
  /** True on the marketing-site embed, false on app.homepoint.co. */
  public_page?: boolean;
  /** Where a Save landed: the buyer's account or this device only. */
  saved_to?: 'account' | 'device';
  /** The place name the buyer picked, or the raw text when they pressed Enter. */
  query?: string;
  /** Google's formatted address for the picked place. */
  place?: string;
  /** False when the text matched no place and the map could not move. */
  resolved?: boolean;
}

export function trackBrowse(
  event: BrowseEvent,
  properties: BrowseEventProperties = {}
): void {
  try {
    posthog.capture(event, properties);
  } catch {
    /* Analytics must never interrupt browsing or signup. */
  }
}

/** The subset of a Google Places result the map's autocomplete requests. */
interface SearchedPlace {
  name?: string;
  formatted_address?: string;
  geometry?: unknown;
}

/**
 * Records the place a buyer searched on the map. Only the chosen place is
 * sent, never keystrokes, since session replay masks the input itself.
 */
export function trackLocationSearched(
  place: SearchedPlace | undefined,
  publicPage: boolean
): void {
  const query = place?.name?.trim();
  if (!query) return;
  trackBrowse('browse_location_searched', {
    query,
    place: place?.formatted_address,
    resolved: Boolean(place?.geometry),
    public_page: publicPage,
  });
}
